import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';

// Components
import Dashboard from './pages/Dashboard';
import Certificates from './pages/Certificates';
import Accounts from './pages/Accounts';
import Devices from './pages/Devices';
import DeviceDetails from './pages/DeviceDetails';
import Reports from './pages/Reports';
import Transfers from './pages/Transfers';
import Landing from './pages/Landing';
import KYC from './pages/KYC';
import RegisterDevice from './pages/RegisterDevice';
import IssuanceRequest from './pages/IssuanceRequest';
import TransferWizard from './pages/TransferWizard';
import CancellationRequest from './pages/CancellationRequest';
import RegisterType from './pages/RegisterType';
import AdminApprovals from './pages/AdminApprovals';
import AuditLog from './pages/AuditLog';
import HubMonitor from './pages/HubMonitor';
import Participants from './pages/Participants';
import Login from './pages/Login';
import MyAccount from './pages/MyAccount';
import MeterPortal from './pages/MeterPortal';
import Navigation from './components/Navigation';
import Header from './components/Header';
import KnowledgeHub from './pages/KnowledgeHub';
import VerifyCertificate from './pages/VerifyCertificate';
import WikiHow from './pages/WikiHow';
import { TourProvider } from './components/TourProvider';

import { AuthProvider, useAuth } from './context/AuthContext';

const ProtectedRoute = ({ children, roles = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const Layout = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const isPublicPage = location.pathname === '/' ||
    location.pathname === '/register' ||
    location.pathname === '/login' ||
    location.pathname === '/register-type' ||
    location.pathname === '/wikihow' ||
    location.pathname.startsWith('/verify/');
  const isWikiHow = location.pathname === '/wikihow';

  return (
    <div className="h-screen bg-[#f1f5f9] flex flex-col overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden min-h-0">
        {!isPublicPage && user && <Navigation />}
        <main className={`flex-1 min-h-0 ${isWikiHow ? 'overflow-hidden' : 'overflow-y-auto'} ${isPublicPage && !isWikiHow ? 'w-full' : ''} ${!isPublicPage ? 'p-8' : ''}`}>
          <div className={`${isWikiHow ? 'h-full' : 'max-w-[1600px] mx-auto min-h-full'}`}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

function App() {
  const [apiHealth, setApiHealth] = useState(true);

  useEffect(() => {
    const checkAPI = async () => {
      try {
        await axios.get(`/health`);
        setApiHealth(true);
      } catch (error) {
        setApiHealth(false);
      }
    };
    checkAPI();
  }, []);

  return (
    <AuthProvider>
      <TourProvider>
        <Router>
          <Layout>
            {!apiHealth && (
              <div className="m-6 bg-red-500/10 border border-red-500/20 text-red-500 px-4 py-3 rounded-xl animate-fade-in text-sm font-bold flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                Backend API Disconnected. Some features may be unavailable.
              </div>
            )}
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/register" element={<KYC />} />
              <Route path="/register-type" element={<RegisterType />} />
              <Route path="/login" element={<Login />} />
              <Route path="/kyc" element={<KYC />} />
              <Route path="/verify/:id" element={<VerifyCertificate />} />
              <Route path="/wikihow" element={<WikiHow />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
              <Route path="/certificates" element={<ProtectedRoute><Certificates /></ProtectedRoute>} />
              <Route path="/certificates/cancel" element={<ProtectedRoute><CancellationRequest /></ProtectedRoute>} />
              <Route path="/issuance/request" element={<ProtectedRoute><IssuanceRequest /></ProtectedRoute>} />
              <Route path="/transfers" element={<ProtectedRoute><Transfers /></ProtectedRoute>} />
              <Route path="/transfers/new" element={<ProtectedRoute><TransferWizard /></ProtectedRoute>} />
              <Route path="/my-account" element={<ProtectedRoute><MyAccount /></ProtectedRoute>} />
              <Route path="/devices" element={<ProtectedRoute><Devices /></ProtectedRoute>} />
              <Route path="/devices/:id" element={<ProtectedRoute><DeviceDetails /></ProtectedRoute>} />
              <Route path="/devices/register" element={<ProtectedRoute><RegisterDevice /></ProtectedRoute>} />
              <Route path="/knowledge-hub" element={<ProtectedRoute><KnowledgeHub /></ProtectedRoute>} />
              <Route path="/accounts" element={<ProtectedRoute roles={['ADMIN', 'ISSUING_BODY']}><Accounts /></ProtectedRoute>} />
              <Route path="/admin/approvals" element={<ProtectedRoute roles={['ADMIN', 'ISSUING_BODY']}><AdminApprovals /></ProtectedRoute>} />
              <Route path="/admin/audit" element={<ProtectedRoute roles={['ADMIN', 'ISSUING_BODY']}><AuditLog /></ProtectedRoute>} />
              <Route path="/admin/hub" element={<ProtectedRoute roles={['ADMIN', 'ISSUING_BODY']}><HubMonitor /></ProtectedRoute>} />
              <Route path="/admin/participants" element={<ProtectedRoute roles={['ADMIN', 'ISSUING_BODY']}><Participants /></ProtectedRoute>} />
              <Route path="/grid/metering" element={<ProtectedRoute roles={['GRID_OPERATOR', 'VVB_AUDITOR', 'ADMIN', 'ISSUING_BODY']}><MeterPortal /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        </Router>
      </TourProvider>
    </AuthProvider>
  );
}

export default App;
